// The photos and videos of a lot's desired state (BL-2815, ADR 070 — Ingest Protocol § 7): the files
// still to send, the ids to remove, and the final order. Pure, so the cancel rule, the position map and
// the settle rule are testable without a store or a network.
import { stagedPosterIdOf } from './offline-attachments';

/** One photo or video picked offline and not yet sent. Its id is the media id on the wire. */
export interface MediaAdd {
  /** The media id: the staged file's id, so a retry sends the same id (ADR 074 — Upload Standard). */
  id: string;
  type: 'photo' | 'video';
  /** The staged file: the photo's original, or the video's file. Absent on a file already in storage. */
  stagedFileId?: string;
  /** A video's staged poster frame, which becomes its thumbnail. */
  stagedPosterFileId?: string;
  /**
   * A file uploaded before the phone went offline (no staged file): it attaches through the media
   * ingest by this path, with no files, because its thumbnail was uploaded beside it at pick time (BL-2816).
   */
  storagePath?: string;
  /** A stored video's poster path. */
  posterStoragePath?: string;
  caption?: string;
  /** Where the drawer placed it before any reorder (used when no order is recorded). */
  sortOrder: number;
  isCover: boolean;
}

/** A lot's photos and videos as the operator wants them after sync. */
export interface LotMedia {
  /** Queued files, in the order they were picked. */
  adds: MediaAdd[];
  /** Ids the lot holds on the server that the operator removed. */
  removed: string[];
  /** Every media id in its final order, or null when the operator never reordered. */
  order: string[] | null;
  /**
   * True when `order` names a photo the server already holds, so the final lot-state request must
   * carry the position map. An order over queued files alone rides each ingest's `position`.
   */
  orderNeedsRequest: boolean;
}

/** What a Save does to a lot's media. */
export interface MediaSave {
  adds: MediaAdd[];
  removes: string[];
  order: string[] | null;
}

/** A lot with no media to send. */
export const EMPTY_MEDIA: LotMedia = { adds: [], removed: [], order: null, orderNeedsRequest: false };

/**
 * @param state - a lot state, possibly saved before media existed.
 * @returns its media, or the empty one.
 */
export function mediaOf(state: { media?: LotMedia }): LotMedia {
  return state.media ?? EMPTY_MEDIA;
}

/**
 * @param media - a lot's media.
 * @returns true when nothing is left to send for it.
 */
export function isMediaEmpty(media: LotMedia): boolean {
  // An order over queued files alone is carried by their ingests, so it is owed only while one waits.
  return media.adds.length === 0 && media.removed.length === 0 && (media.order == null || !media.orderNeedsRequest);
}

/**
 * The staged files a queued add owns. A staged photo also owns the thumbnail its pick staged beside it, under the
 * id a video's poster uses (BL-2682). An id with no file behind it (a photo staged by an earlier build) deletes as a no-op.
 */
export function filesOfAdd(add: MediaAdd): string[] {
  const thumbnail = add.type === 'photo' && add.stagedFileId != null ? stagedPosterIdOf(add.stagedFileId) : null;
  return [add.stagedFileId, add.stagedPosterFileId, thumbnail].filter((id): id is string => id != null);
}

/**
 * Fold one Save's media changes into a lot's media. Removing a queued add cancels it (it never
 * reaches the server); removing any other id records it once. The order replaces the last one,
 * without the ids removed.
 *
 * @param media - the lot's media so far.
 * @param save - the Save's adds, removes and order.
 * @returns the new media, and the staged files of every add the Save cancelled (to delete).
 */
export function foldMediaSave(media: LotMedia, save: MediaSave): { media: LotMedia; cancelledFileIds: string[] } {
  const adds = [...media.adds];
  for (const add of save.adds) {
    const at = adds.findIndex((a) => a.id === add.id);
    if (at === -1) adds.push(add);
    else adds[at] = add;
  }
  const removed = [...media.removed];
  const cancelledFileIds: string[] = [];
  const gone = new Set<string>();
  for (const id of save.removes) {
    gone.add(id);
    const at = adds.findIndex((a) => a.id === id);
    if (at !== -1) {
      cancelledFileIds.push(...filesOfAdd(adds[at]));
      adds.splice(at, 1);
    } else if (!removed.includes(id)) {
      removed.push(id);
    }
  }
  const queued = new Set(adds.map((a) => a.id));
  const rawOrder = save.order ?? media.order;
  const order = rawOrder == null ? null : rawOrder.filter((id) => !gone.has(id));
  // A file attached by path has no ingest placement, so an order over it needs the position map too.
  const orderNeedsRequest = order != null && (order.some((id) => !queued.has(id)) || adds.some((a) => a.stagedFileId == null));
  return { media: { adds, removed, order, orderNeedsRequest }, cancelledFileIds };
}

/**
 * @param media - the lot's media.
 * @param id - a queued add's media id.
 * @returns the media without that add (it was sent, or the server said it was removed).
 */
export function withoutAdd(media: LotMedia, id: string): LotMedia {
  const adds = media.adds.filter((a) => a.id !== id);
  // `orderNeedsRequest` was decided when the order was recorded and does not change as adds go up. An
  // order that only placed queued files has nothing left to say once the last of them is sent.
  return { ...media, adds, order: adds.length === 0 && !media.orderNeedsRequest ? null : media.order };
}

/**
 * Forget every queued add of a lot (its photo or video is never sent): the lot was moved or removed,
 * or the server no longer holds it (BL-2815, RAC 2).
 *
 * @param media - a lot's media.
 * @returns the media with no adds (removals kept), and the staged files the dropped adds named, to delete.
 */
export function withoutAdds(media: LotMedia): { media: LotMedia; files: string[] } {
  const dropped = new Set(media.adds.map((a) => a.id));
  const emptied = media.adds.reduce((cur, a) => withoutAdd(cur, a.id), media);
  // A recorded order must not name a media id the server never received.
  const order = emptied.order?.filter((id) => !dropped.has(id)) ?? null;
  return { media: { ...emptied, order: order != null && order.length > 0 ? order : null }, files: allStagedFiles(media) };
}

/**
 * The `position` and `isCover` of one ingest request: the add's place in the recorded order (the
 * first is the cover), else the place the drawer gave it.
 *
 * @param media - the lot's media.
 * @param add - the queued add being sent.
 * @returns the meta the ingest carries.
 */
export function ingestPlacement(media: LotMedia, add: MediaAdd): { position: number; isCover: boolean } {
  const at = media.order?.indexOf(add.id) ?? -1;
  return at === -1 ? { position: add.sortOrder, isCover: add.isCover } : { position: at, isCover: at === 0 };
}

/**
 * @param media - the lot's media.
 * @returns the id-to-index map for the lot-state request, or null when the order rides the ingests.
 */
export function positionsToSend(media: LotMedia): Record<string, number> | null {
  if (media.order == null || !media.orderNeedsRequest) return null;
  return Object.fromEntries(media.order.map((id, index) => [id, index]));
}

/**
 * The lot-state request landed with these removals and this order: drop what it carried, and keep
 * anything the operator changed meanwhile.
 *
 * @param media - the lot's media as it is now.
 * @param sent - the removed ids and the position map the request carried (null when none).
 * @returns the media still owed.
 */
export function settleMedia(media: LotMedia, sent: { removedMediaIds: string[]; positions: Record<string, number> | null }): LotMedia {
  const removed = media.removed.filter((id) => !sent.removedMediaIds.includes(id));
  const sentOrder = sent.positions == null ? null : Object.keys(sent.positions).sort((a, b) => (sent.positions?.[a] ?? 0) - (sent.positions?.[b] ?? 0));
  const sameOrder = sentOrder != null && media.order != null && sentOrder.length === media.order.length && sentOrder.every((id, i) => id === media.order?.[i]);
  const order = sameOrder ? null : media.order;
  return { ...media, removed, order, orderNeedsRequest: sameOrder ? false : media.orderNeedsRequest };
}

/**
 * @param media - the lot's media.
 * @param ids - media ids being removed.
 * @returns the staged files those ids' queued adds own.
 */
export function stagedFilesOfAdds(media: LotMedia, ids: readonly string[]): string[] {
  return media.adds.filter((a) => ids.includes(a.id)).flatMap(filesOfAdd);
}

/**
 * @param media - the lot's media.
 * @returns every staged file the queued adds own, for the open-time sweep and a discard.
 */
export function allStagedFiles(media: LotMedia): string[] {
  return media.adds.flatMap(filesOfAdd);
}
